import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ComposedChart, Line,
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';
import { Base_URL2 } from '../../url';
import { useAuth } from '../../Context/AuthContext';
import '../../Styling/Inventory.css';

/* ─────────────────────────────────────────────
   FIELD KEY REFERENCE  (actual API response)
   Organisation_Id   Org_Description
   Comapny_Code      Company_Code_Description   ← typo in API, kept as-is
   OfficeCode        Office_code_Description
   store_code        store_code_Description
   Item_type         item_type_Description
   Unit              Unit_Description
   Item_group        Item_group_Description
   Item_Code         Item_code_Description
   Total_Item_Quantity  Total_Item_Value
───────────────────────────────────────────── */

const PALETTE = [
  '#2563eb','#059669','#d97706','#dc2626',
  '#7c3aed','#0891b2','#65a30d','#ea580c',
  '#4f46e5','#0d9488','#ca8a04','#b91c1c',
];

const fmt = (v) => (v / 100_000).toFixed(2);
const toLakhs = (v) => v / 100_000;

/* ─── tiny helpers ─── */
const uniq = (arr, keyFn, mapFn) =>
  [...new Map(arr.map((x) => [keyFn(x), mapFn(x)])).values()];

/* ─── custom tooltip ─── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="id-tooltip">
      {label && <p className="id-tooltip-label">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: ₹{Number(p.value).toFixed(2)} L
        </p>
      ))}
    </div>
  );
};

/* ─── KPI card ─── */
const KpiCard = ({ title, value, sub, icon, accent }) => (
  <div className={`id-kpi id-kpi--${accent}`}>
    <div className="id-kpi__icon">{icon}</div>
    <div className="id-kpi__body">
      <span className="id-kpi__label">{title}</span>
      <span className="id-kpi__value">₹{fmt(value)} L</span>
      <span className="id-kpi__sub">{sub}</span>
    </div>
  </div>
);

/* ─── section heading ─── */
const SectionHeading = ({ children }) => (
  <h2 className="id-section-heading">{children}</h2>
);

/* ─── chart wrapper ─── */
const ChartCard = ({ title, loading, empty, children }) => (
  <div className="id-chart-card">
    <p className="id-chart-title">{title}</p>
    {loading ? (
      <div className="id-spinner-wrap"><span className="id-spinner" /></div>
    ) : empty ? (
      <div className="id-empty">
        <i className="ti ti-chart-bar" aria-hidden="true" />
        <span>No data available</span>
      </div>
    ) : children}
  </div>
);

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const InventoryDashboard = () => {
  const { user } = useAuth();

  /* ── filter state ── */
  const [filters, setFilters] = useState({
    orgId:        String(user?.organisationId || ''),
    companyCode:  '',
    officeCode:   '',
    storeCode:    '',
    postingDate:  '',
  });

  const [filterDisplay, setFilterDisplay] = useState({
    orgName: '', companyName: '', officeName: '', storeName: '',
  });

  /* ── dropdown lists ── */
  const [orgList,     setOrgList]     = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [officeList,  setOfficeList]  = useState([]);
  const [storeList,   setStoreList]   = useState([]);

  /* ── data ── */
  const [allData,   setAllData]   = useState([]);
  const [tableData, setTableData] = useState([]);

  /* ── kpi + chart data ── */
  const [kpi, setKpi] = useState({
    totalValue: 0, finishedValue: 0, rawValue: 0, packingValue: 0,
  });
  const [charts, setCharts] = useState({
    office: [], store: [], itemType: [], unit: [], itemGroup: [],
    topSku: [], movement: [],
  });

  /* ── ui state ── */
  const [loading,     setLoading]     = useState(false);
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [xlsLoading,  setXlsLoading]  = useState(false);
  const [error,       setError]       = useState(null);
  const [page,        setPage]        = useState(1);
  const PAGE_SIZE = 50;
  const dashRef = useRef(null);

  /* ─────────────────────────────────────────
     DATA HELPERS — all field names match the
     actual API response keys exactly
  ───────────────────────────────────────── */
  const buildChartData = useCallback((data, codeKey, descKey, limit = 10) => {
    const map = {};
    data.forEach((item) => {
      const code = item[codeKey];
      const desc = item[descKey];
      const val  = toLakhs(item.Total_Item_Value || 0);
      if (code && desc && val > 0) {
        const k = `${code}||${desc}`;
        if (!map[k]) map[k] = { name: desc.length > 22 ? `${desc.slice(0, 22)}…` : desc, fullName: desc, value: 0 };
        map[k].value += val;
      }
    });
    return Object.values(map)
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  }, []);

  const applyFilters = useCallback((data, f) => {
    return data.filter((item) => (
      (!f.orgId        || String(item.Organisation_Id) === String(f.orgId)) &&
      (!f.companyCode  || String(item.Comapny_Code)    === String(f.companyCode)) &&
      (!f.officeCode   || String(item.OfficeCode)      === String(f.officeCode)) &&
      (!f.storeCode    || String(item.store_code)      === String(f.storeCode))
    ));
  }, []);

  const computeDashboard = useCallback((filtered) => {
    const total = filtered.reduce((s, i) => s + (i.Total_Item_Value || 0), 0);

    /* item-type sums — match on Item_type field */
    const sumByType = (codes) =>
      filtered
        .filter((i) => codes.some((c) => String(i.Item_type || '').toUpperCase().startsWith(c)))
        .reduce((s, i) => s + (i.Total_Item_Value || 0), 0);

    setKpi({
      totalValue:    total,
      finishedValue: sumByType(['FM', 'FG', 'FINI']),
      rawValue:      sumByType(['RA', 'RM', 'RAW']),
      packingValue:  sumByType(['PA', 'PKG', 'PACK']),
    });

    setCharts({
      office:   buildChartData(filtered, 'OfficeCode',    'Office_code_Description'),
      store:    buildChartData(filtered, 'store_code',    'store_code_Description'),
      itemType: buildChartData(filtered, 'Item_type',     'item_type_Description'),
      unit:     buildChartData(filtered, 'Unit',          'Unit_Description'),
      itemGroup:buildChartData(filtered, 'Item_group',    'Item_group_Description'),
      topSku:   buildChartData(filtered, 'Item_Code',     'Item_code_Description', 8),
      movement: charts.movement, // fetched separately
    });
  }, [buildChartData, charts.movement]);

  /* ─────────────────────────────────────────
     FETCH
  ───────────────────────────────────────── */
  const fetchMovement = useCallback(async () => {
    try {
      const res = await axios.get(`${Base_URL2}/InventoryDashboard/get-inventory-status`);
      console.log(res?.data);
      const totals = res?.data?.TotalsByPostingKey|| [];
      const n3 = totals.find((t) => t.PostingKey === 'N3');
      const n4 = totals.find((t) => t.PostingKey === 'N4');
      setCharts((prev) => ({
        ...prev,
        movement: [{
          name:     'Movement',
          received: toLakhs(n3?.TotalAmount || 0),
          issued:   toLakhs(n4?.TotalAmount || 0),
        }],
      }));
    } catch (e) {
      console.error('Movement fetch error:', e);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${Base_URL2}/InventoryDashboard/GetInventoryDashboardData`);
      const data = res.data || [];
      setAllData(data);
      setTableData(data);

      /* build org list using correct field names */
      const orgs = uniq(data,
        (x) => x.Organisation_Id,
        (x) => ({ orgId: x.Organisation_Id, orgName: x.Org_Description }),
      );
      setOrgList(orgs);

      /* pre-select user's org */
      if (user?.organisationId) {
        const userOrg = orgs.find((o) => String(o.orgId) === String(user.organisationId));
        if (userOrg) {
          setFilterDisplay((p) => ({ ...p, orgName: userOrg.orgName }));
          const companies = uniq(
            data.filter((x) => String(x.Organisation_Id) === String(user.organisationId)),
            (x) => x.Comapny_Code,
            (x) => ({ companyCode: x.Comapny_Code, companyName: x.Company_Code_Description }),
          );
          setCompanyList(companies);
        }
      }

      fetchMovement();
    } catch (e) {
      console.error('Fetch error:', e);
      setError('Failed to load inventory data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, fetchMovement]);

  /* ── initial load ── */
  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── recompute on filter change ── */
  useEffect(() => {
    if (!allData.length) return;
    const filtered = applyFilters(allData, filters);
    setTableData(filtered);
    setPage(1);
    computeDashboard(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, filters]);

  /* ─────────────────────────────────────────
     FILTER HANDLERS
  ───────────────────────────────────────── */
  const handleOrgChange = (e) => {
    const orgId = e.target.value;
    const org   = orgList.find((o) => String(o.orgId) === orgId);
    setFilters({ orgId, companyCode: '', officeCode: '', storeCode: '', postingDate: filters.postingDate });
    setFilterDisplay({ orgName: org?.orgName || '', companyName: '', officeName: '', storeName: '' });
    setCompanyList(orgId
      ? uniq(allData.filter((x) => String(x.Organisation_Id) === orgId),
          (x) => x.Comapny_Code,
          (x) => ({ companyCode: x.Comapny_Code, companyName: x.Company_Code_Description }))
      : []);
    setOfficeList([]); setStoreList([]);
  };

  const handleCompanyChange = (e) => {
    const companyCode = e.target.value;
    const co = companyList.find((c) => String(c.companyCode) === companyCode);
    setFilters((f) => ({ ...f, companyCode, officeCode: '', storeCode: '' }));
    setFilterDisplay((d) => ({ ...d, companyName: co?.companyName || '', officeName: '', storeName: '' }));
    setOfficeList(companyCode && filters.orgId
      ? uniq(
          allData.filter((x) =>
            String(x.Organisation_Id) === filters.orgId &&
            String(x.Comapny_Code)    === companyCode),
          (x) => x.OfficeCode,
          (x) => ({ officeCode: x.OfficeCode, officeName: x.Office_code_Description }),
        ).filter((o) => o.officeCode)
      : []);
    setStoreList([]);
  };

  const handleOfficeChange = (e) => {
    const officeCode = e.target.value;
    const off = officeList.find((o) => String(o.officeCode) === officeCode);
    setFilters((f) => ({ ...f, officeCode, storeCode: '' }));
    setFilterDisplay((d) => ({ ...d, officeName: off?.officeName || '', storeName: '' }));
    setStoreList(officeCode
      ? uniq(
          allData.filter((x) => String(x.OfficeCode) === officeCode),
          (x) => x.store_code,
          (x) => ({ storeCode: x.store_code, storeName: x.store_code_Description }),
        ).filter((s) => s.storeCode)
      : []);
  };

  const handleStoreChange = (e) => {
    const storeCode = e.target.value;
    const st = storeList.find((s) => String(s.storeCode) === storeCode);
    setFilters((f) => ({ ...f, storeCode }));
    setFilterDisplay((d) => ({ ...d, storeName: st?.storeName || '' }));
  };

  const handleReset = () => {
    setFilters({ orgId: String(user?.organisationId || ''), companyCode: '', officeCode: '', storeCode: '', postingDate: '' });
    setFilterDisplay({ orgName: '', companyName: '', officeName: '', storeName: '' });
    setOfficeList([]); setStoreList([]);
  };

  /* ─────────────────────────────────────────
     EXPORT
  ───────────────────────────────────────── */
  const handlePDF = async () => {
    setPdfLoading(true);
    try {
      const canvas = await html2canvas(dashRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pw / canvas.width, ph / canvas.height);
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.8), 'JPEG', 0, 0, canvas.width * ratio, canvas.height * ratio);
      pdf.save(`Inventory_${filters.orgId || 'All'}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      setError('PDF generation failed.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCSV = () => {
    setXlsLoading(true);
    try {
      const headers = ['Org ID','Organisation','Company Code','Company','Office Code','Office','Store Code','Store','Item Type','Item Type Desc','Unit','Unit Desc','Item Group','Item Group Desc','Item Code','Item Description','Qty','Value (₹)'];
      const rows = tableData.map((i) => [
        i.Organisation_Id, i.Org_Description,
        i.Comapny_Code,    i.Company_Code_Description,
        i.OfficeCode,      i.Office_code_Description,
        i.store_code,      i.store_code_Description,
        i.Item_type,       i.item_type_Description,
        i.Unit,            i.Unit_Description,
        i.Item_group,      i.Item_group_Description,
        i.Item_Code,       i.Item_code_Description,
        i.Total_Item_Quantity, i.Total_Item_Value,
      ]);
      const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `Inventory_${(filterDisplay.orgName || 'All').replace(/\W+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError('Export failed.');
    } finally {
      setXlsLoading(false);
    }
  };

  /* ─────────────────────────────────────────
     TABLE PAGINATION
  ───────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(tableData.length / PAGE_SIZE));
  const pageData   = tableData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ─────────────────────────────────────────
     RENDER CHART HELPERS
  ───────────────────────────────────────── */
  const renderPie = (data, title) => (
    <ChartCard title={title} loading={loading} empty={!data.length}>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" outerRadius={90} innerRadius={52}
               dataKey="value" labelLine={false}
               label={({ percent }) => percent > 0.06 ? `${(percent * 100).toFixed(0)}%` : ''}>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );

  const renderBar = (data, title) => (
    <ChartCard title={title} loading={loading} empty={!data.length}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 60, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis dataKey="name" angle={-40} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v.toFixed(1)}L`} width={56} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[3, 3, 0, 0]}>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );

  const renderComposed = (data, title) => (
    <ChartCard title={title} loading={loading} empty={!data.length}>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 60, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis dataKey="name" angle={-40} textAnchor="end" tick={{ fontSize: 10 }} interval={0} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v.toFixed(1)}L`} width={56} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" fill="#2563eb" radius={[3, 3, 0, 0]} />
          <Line type="monotone" dataKey="value" stroke="#dc2626" strokeWidth={2} dot={{ r: 3, fill: '#dc2626' }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );

  const renderMovement = (data, title) => (
    <ChartCard title={title} loading={loading} empty={!data.length}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 20, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v.toFixed(1)}L`} width={56} />
          <Tooltip content={<CustomTooltip />} />
          <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="received" name="Received (N3)" fill="#059669" radius={[3, 3, 0, 0]} />
          <Bar dataKey="issued"   name="Issued (N4)"   fill="#dc2626" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );

  /* ─────────────────────────────────────────
     JSX
  ───────────────────────────────────────── */
  return (
    <div className="id-root" ref={dashRef}>

      {/* ── top bar ── */}
      <header className="id-header">
        <div className="id-header__left">
          <span className="id-header__eyebrow">Enterprise ERP</span>
          <h1 className="id-header__title">Inventory Dashboard</h1>
        </div>
        <div className="id-header__actions">
          <button className="id-btn id-btn--ghost" onClick={fetchAll} disabled={loading} title="Refresh">
            <i className="ti ti-refresh" aria-hidden="true" />
          </button>
          <button className="id-btn id-btn--outline" onClick={handleCSV} disabled={xlsLoading || !tableData.length}>
            <i className="ti ti-download" aria-hidden="true" />
            {xlsLoading ? 'Exporting…' : 'Export CSV'}
          </button>
          <button className="id-btn id-btn--primary" onClick={handlePDF} disabled={pdfLoading || loading}>
            <i className="ti ti-file" aria-hidden="true" />
            {pdfLoading ? 'Generating…' : 'Export PDF'}
          </button>
        </div>
      </header>

      {/* ── error banner ── */}
      {error && (
        <div className="id-error" role="alert">
          <i className="ti ti-alert-circle" aria-hidden="true" />
          {error}
          <button className="id-error__close" onClick={() => setError(null)} aria-label="Dismiss">
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* ── filters ── */}
      <section className="id-filters" aria-label="Dashboard filters">
        <div className="id-filters__grid">

          <div className="id-field">
            <label className="id-label">Organisation</label>
            <select className="id-select" value={filters.orgId} onChange={handleOrgChange}>
              <option value="">All Organisations</option>
              {orgList.map((o) => (
                <option key={o.orgId} value={o.orgId}>{o.orgId} – {o.orgName}</option>
              ))}
            </select>
          </div>

          <div className="id-field">
            <label className="id-label">Company</label>
            <select className="id-select" value={filters.companyCode} onChange={handleCompanyChange} disabled={!filters.orgId}>
              <option value="">All Companies</option>
              {companyList.map((c) => (
                <option key={c.companyCode} value={c.companyCode}>{c.companyCode} – {c.companyName}</option>
              ))}
            </select>
          </div>

          <div className="id-field">
            <label className="id-label">Office</label>
            <select className="id-select" value={filters.officeCode} onChange={handleOfficeChange} disabled={!filters.companyCode}>
              <option value="">All Offices</option>
              {officeList.map((o) => (
                <option key={o.officeCode} value={o.officeCode}>{o.officeCode} – {o.officeName}</option>
              ))}
            </select>
          </div>

          <div className="id-field">
            <label className="id-label">Store</label>
            <select className="id-select" value={filters.storeCode} onChange={handleStoreChange} disabled={!filters.officeCode}>
              <option value="">All Stores</option>
              {storeList.map((s) => (
                <option key={s.storeCode} value={s.storeCode}>{s.storeCode} – {s.storeName}</option>
              ))}
            </select>
          </div>

          <div className="id-field">
            <label className="id-label">Posting Date</label>
            <input
              type="date" className="id-input"
              value={filters.postingDate}
              onChange={(e) => setFilters((f) => ({ ...f, postingDate: e.target.value }))}
            />
          </div>

          <div className="id-field id-field--reset">
            <label className="id-label" style={{ opacity: 0 }}>Reset</label>
            <button className="id-btn id-btn--ghost id-btn--full" onClick={handleReset}>
              <i className="ti ti-x" aria-hidden="true" /> Reset filters
            </button>
          </div>
        </div>

        {/* active filter chips */}
        {(filterDisplay.orgName || filterDisplay.companyName || filterDisplay.officeName || filterDisplay.storeName) && (
          <div className="id-chips">
            {filterDisplay.orgName     && <span className="id-chip">Org: {filterDisplay.orgName}</span>}
            {filterDisplay.companyName && <span className="id-chip">Co: {filterDisplay.companyName}</span>}
            {filterDisplay.officeName  && <span className="id-chip">Office: {filterDisplay.officeName}</span>}
            {filterDisplay.storeName   && <span className="id-chip">Store: {filterDisplay.storeName}</span>}
          </div>
        )}
      </section>

      {/* ── KPI cards ── */}
      <section className="id-kpi-grid" aria-label="Key performance indicators">
        <KpiCard title="Total Inventory"   value={kpi.totalValue}    sub="All item types combined" icon="💰" accent="blue" />
        <KpiCard title="Finished Goods"    value={kpi.finishedValue} sub="FM00 / FG item types"    icon="🏭" accent="green" />
        <KpiCard title="Raw Materials"     value={kpi.rawValue}      sub="RA00 / RM item types"    icon="⚙️" accent="amber" />
        <KpiCard title="Packing Materials" value={kpi.packingValue}  sub="PA00 / PKG item types"   icon="📦" accent="red" />
      </section>

      {/* ── charts row 1 ── */}
      <SectionHeading>Inventory Distribution</SectionHeading>
      <div className="id-chart-grid">
        {renderPie(charts.office,   'Office-wise Distribution')}
        {renderBar(charts.store,    'Store-wise Inventory')}
        {renderComposed(charts.itemType, 'Item Type Analysis')}
      </div>

      {/* ── charts row 2 ── */}
      <SectionHeading>Movement & Composition</SectionHeading>
      <div className="id-chart-grid">
        {renderMovement(charts.movement,  'Received vs Issued (N3/N4)')}
        {renderPie(charts.unit,           'Unit of Measure Mix')}
        {renderBar(charts.topSku,         'Top 8 SKUs by Value')}
      </div>

      {/* ── detail table ── */}
      <section className="id-table-section">
        <div className="id-table-header">
          <div>
            <h2 className="id-table-title">Inventory Detail</h2>
            <p className="id-table-meta">
              {tableData.length.toLocaleString()} records
              &nbsp;·&nbsp;
              Total ₹{fmt(kpi.totalValue)} L
              &nbsp;·&nbsp;
              Page {page} of {totalPages}
            </p>
          </div>
        </div>

        <div className="id-table-wrap">
          <table className="id-table">
            <thead>
              <tr>
                <th>Org</th>
                <th>Company</th>
                <th>Office</th>
                <th>Store</th>
                <th>Item Type</th>
                <th>Unit</th>
                <th>Item Group</th>
                <th>Item Code</th>
                <th>Description</th>
                <th className="id-table__num">Qty</th>
                <th className="id-table__num">Value (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="id-table__placeholder">
                    <span className="id-spinner" /> Loading data…
                  </td>
                </tr>
              ) : pageData.length === 0 ? (
                <tr>
                  <td colSpan={11} className="id-table__placeholder">
                    <i className="ti ti-inbox" aria-hidden="true" />
                    No records match the current filters.
                  </td>
                </tr>
              ) : pageData.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'id-tr--even' : ''}>
                  <td title={item.Org_Description}>{item.Org_Description || '–'}</td>
                  <td title={item.Company_Code_Description}>{item.Comapny_Code || '–'}</td>
                  <td title={item.Office_code_Description}>{item.OfficeCode || '–'}</td>
                  <td title={item.store_code_Description}>{item.store_code || '–'}</td>
                  <td>
                    <span className="id-badge">{item.item_type_Description || item.Item_type || '–'}</span>
                  </td>
                  <td>{item.Unit_Description || item.Unit || '–'}</td>
                  <td title={item.Item_group_Description}>{item.Item_group || '–'}</td>
                  <td className="id-table__code">{item.Item_Code || '–'}</td>
                  <td title={item.Item_code_Description}>
                    {(item.Item_code_Description || '–').slice(0, 32)}
                    {(item.Item_code_Description || '').length > 32 ? '…' : ''}
                  </td>
                  <td className="id-table__num">{(item.Total_Item_Quantity || 0).toLocaleString()}</td>
                  <td className="id-table__num id-table__value">
                    ₹{(item.Total_Item_Value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        {totalPages > 1 && (
          <div className="id-pagination">
            <button className="id-btn id-btn--ghost id-btn--sm" onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button className="id-btn id-btn--ghost id-btn--sm" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹</button>
            <span className="id-pagination__info">{page} / {totalPages}</span>
            <button className="id-btn id-btn--ghost id-btn--sm" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>›</button>
            <button className="id-btn id-btn--ghost id-btn--sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        )}
      </section>
    </div>
  );
};

export default InventoryDashboard;